import { useEffect, useRef, useState } from "react";
import { GripVertical, VolumeX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatTimecode, parseTimecode, type Clip } from "@rec/shared";
import { clipDurationMs, outputOffsets } from "@/lib/timeline";
import { InlineRename } from "@/components/InlineRename";
import { cn } from "@/lib/utils";

/** "1:23", "1:02:03", or bare seconds ("45"). Null when unreadable. */
function parseLoose(value: string): number | null {
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed) * 1000;
  return parseTimecode(trimmed);
}

/**
 * One editable timecode. Commits on Enter or blur, reverts on Escape or
 * nonsense — a trim field must never half-apply while someone is typing.
 */
function TimecodeField({
  valueMs,
  label,
  onCommit,
}: {
  valueMs: number;
  label: string;
  onCommit: (ms: number) => void;
}) {
  const [text, setText] = useState(formatTimecode(valueMs));

  // External changes (edge-drag trims, undo) win over stale local text.
  useEffect(() => setText(formatTimecode(valueMs)), [valueMs]);

  const commit = () => {
    const parsed = parseLoose(text);
    if (parsed === null) {
      setText(formatTimecode(valueMs));
      return;
    }
    onCommit(parsed);
  };

  return (
    <label className="inline-flex items-center gap-1">
      <span className="text-[10px] text-text-faint">{label}</span>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") setText(formatTimecode(valueMs));
        }}
        onClick={(e) => e.stopPropagation()}
        size={7}
        inputMode="numeric"
        className="rounded border border-border bg-bg px-1 py-0.5 text-center font-mono text-[11px] tabular-nums text-text focus:border-accent focus:outline-none"
      />
    </label>
  );
}

/**
 * The timeline as a list: one row per clip, in playback order.
 *
 * The strip and this list are two views of the same array. The strip is
 * spatial — good for trimming and seeing proportions; the list is legible —
 * good for reading what comes after what, and for reordering without pixel
 * aim. A split therefore shows up here as two rows the moment it happens,
 * which is also what makes the list honest about what a cut did.
 *
 * Reordering drags rows with the grip (pointer math, same one-commit-per-
 * gesture contract as the strip, so undo stays one step per human action).
 * The grip works by touch too — on phones this list IS the reorder path,
 * since the strip deliberately gives touch to panning.
 */
export function ElementList({
  clips,
  displayTitles,
  thumbnails,
  selectedId,
  onSelect,
  onSeek,
  onReorder,
  onRenameClip,
  sourceDurations,
  onTrim,
}: {
  clips: Clip[];
  /** Per-project alias falling back to library title, keyed by recording id. */
  displayTitles: Map<string, string>;
  thumbnails: Map<string, string | null>;
  selectedId: string | null;
  onSelect: (clipId: string) => void;
  onSeek: (outputMs: number) => void;
  /** Commit a completed drag: move the clip at `from` before position `to`. */
  onReorder: (from: number, to: number) => void;
  /** Name ONE part — two sections cut from one video are different things. */
  onRenameClip: (clipId: string, name: string) => void;
  /** Real source lengths, for clamping numeric trims. */
  sourceDurations: Map<string, number>;
  /** Set one edge of a clip to an exact source time. */
  onTrim: (clipId: string, edge: "start" | "end", sourceMs: number) => void;
}) {
  const { t } = useTranslation(["studio", "common"]);
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [drag, setDrag] = useState<{
    from: number;
    /** Pre-removal insertion index; `clips.length` = after everything. */
    to: number;
    /** Armed only after real travel — a twitch on the grip is not a drag. */
    armed: boolean;
    startY: number;
  } | null>(null);
  const dragRef = useRef<typeof drag>(null);
  dragRef.current = drag;

  const offsets = outputOffsets(clips);

  /** How many clips each source contributes, for the "part k/n" badge. */
  const partCounts = new Map<string, number>();
  for (const clip of clips) {
    partCounts.set(clip.recordingId, (partCounts.get(clip.recordingId) ?? 0) + 1);
  }
  const partIndex = new Map<string, number>();

  const startDrag = (from: number) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDrag({ from, to: from, armed: false, startY: e.clientY });

    /**
     * Auto-scroll the list while the drag holds near its edges, so a row can
     * travel further than the visible panel. Local state is safe here —
     * these closures live for the whole drag, unlike an effect that
     * re-creates per state change.
     */
    const scroller = (() => {
      let el: HTMLElement | null = rowRefs.current[from] ?? null;
      while (el && el !== document.body) {
        const overflow = getComputedStyle(el).overflowY;
        if (overflow === "auto" || overflow === "scroll") return el;
        el = el.parentElement;
      }
      return null;
    })();
    let lastY = e.clientY;
    let raf = 0;
    const EDGE = 36;
    const autoScroll = () => {
      if (scroller && dragRef.current?.armed) {
        const rect = scroller.getBoundingClientRect();
        const past =
          lastY < rect.top + EDGE
            ? lastY - (rect.top + EDGE)
            : lastY > rect.bottom - EDGE
              ? lastY - (rect.bottom - EDGE)
              : 0;
        if (past !== 0) {
          scroller.scrollTop += Math.max(-16, Math.min(16, past / 3));
          place(lastY);
        }
      }
      raf = requestAnimationFrame(autoScroll);
    };
    raf = requestAnimationFrame(autoScroll);

    /** Recompute the insertion slot for a pointer at `clientY`. */
    const place = (clientY: number) => {
      const current = dragRef.current;
      if (!current?.armed) return;
      let to = clips.length;
      for (let i = 0; i < clips.length; i++) {
        const rect = rowRefs.current[i]?.getBoundingClientRect();
        if (!rect) continue;
        if (clientY < rect.top + rect.height / 2) {
          to = i;
          break;
        }
      }
      if (to !== current.to) setDrag({ ...current, to });
    };

    const onMove = (ev: PointerEvent) => {
      lastY = ev.clientY;
      const current = dragRef.current;
      if (!current) return;
      // Real travel before anything is considered a drag: without this, the
      // few pixels of pressing the grip committed silent reorders.
      if (!current.armed) {
        if (Math.abs(ev.clientY - current.startY) < 5) return;
        setDrag({ ...current, armed: true });
      }
      place(ev.clientY);
    };

    const onUp = () => {
      const current = dragRef.current;
      if (current?.armed) onReorder(current.from, current.to);
      setDrag(null);
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
  };

  if (clips.length === 0) {
    return (
      <p className="px-1 py-2 text-[12px] leading-relaxed text-text-faint">
        {t("elements.empty")}
      </p>
    );
  }

  return (
    <div className="space-y-1">
      {clips.map((clip, index) => {
        const count = partCounts.get(clip.recordingId)!;
        const part = (partIndex.get(clip.recordingId) ?? 0) + 1;
        partIndex.set(clip.recordingId, part);

        const isDragged = drag?.armed && drag.from === index;
        // The seam marks where the drop lands: above the row at `to`, or
        // under the last row for the end slot. Hidden when the drop would
        // change nothing, so the seam never promises a no-op and moves.
        const dropChanges =
          drag?.armed && drag.to !== drag.from && drag.to !== drag.from + 1;
        const seamAbove = dropChanges && drag!.to === index;
        const seamBelow = dropChanges && index === clips.length - 1 && drag!.to === clips.length;

        return (
          <div key={clip.id} className="relative">
            {seamAbove ? (
              <div className="absolute -top-1 right-0 left-0 h-0.5 rounded bg-accent" />
            ) : null}
            {seamBelow ? (
              <div className="absolute right-0 -bottom-1 left-0 h-0.5 rounded bg-accent" />
            ) : null}
            <div
              ref={(el) => {
                rowRefs.current[index] = el;
              }}
              onClick={() => {
                onSelect(clip.id);
                onSeek(offsets[index]!);
              }}
              className={cn(
                "flex cursor-pointer items-center gap-2 rounded-lg border p-1.5 transition-colors",
                clip.id === selectedId
                  ? "border-accent bg-accent-subtle/20"
                  : "border-border hover:border-border-strong hover:bg-surface-hover",
                isDragged && "opacity-50",
              )}
            >
              <button
                type="button"
                onPointerDown={startDrag(index)}
                onClick={(e) => e.stopPropagation()}
                aria-label={t("elements.reorderPart", { number: index + 1 })}
                title={t("elements.dragToReorder")}
                // touch-action none: this grip is the touch reorder path, so
                // it must win against the list's own scrolling.
                className="shrink-0 cursor-grab touch-none rounded p-1 text-text-faint hover:bg-surface-hover hover:text-text active:cursor-grabbing"
              >
                <GripVertical size={13} />
              </button>

              {thumbnails.get(clip.recordingId) ? (
                <img
                  src={thumbnails.get(clip.recordingId)!}
                  alt=""
                  loading="lazy"
                  className="h-8 w-14 shrink-0 rounded object-cover"
                />
              ) : (
                <div className="h-8 w-14 shrink-0 rounded bg-bg-subtle" />
              )}

              <div className="min-w-0 flex-1">
                <div
                  // w-fit: only the name itself swallows clicks (renaming is
                  // an edit, not a row click) — full width made the top third
                  // of every row a dead zone that neither renamed nor seeked.
                  className="flex w-fit max-w-full items-center gap-1.5 text-[12.5px] font-medium text-text"
                  onClick={(e) => e.stopPropagation()}
                >
                  <InlineRename
                    value={
                      clip.label ?? displayTitles.get(clip.recordingId) ?? t("elements.fallbackTitle")
                    }
                    label={t("elements.namePart")}
                    onRename={(next) => onRenameClip(clip.id, next)}
                    inputClassName="w-full"
                  />
                  {count > 1 ? (
                    <span className="shrink-0 rounded bg-bg-subtle px-1 py-0.5 text-[10px] text-text-faint">
                      {part}/{count}
                    </span>
                  ) : null}
                </div>
                <p className="flex items-center gap-1.5 font-mono text-[10.5px] tabular-nums text-text-faint">
                  <span>{formatTimecode(offsets[index]!)}</span>
                  <span>· {formatTimecode(clipDurationMs(clip))}</span>
                  {clip.muted ? <VolumeX size={10} aria-label={t("elements.muted")} /> : null}
                  {clip.transitionIn ? (
                    <span
                      className="rounded bg-bg-subtle px-1 text-[9.5px]"
                      title={t("elements.hasTransition")}
                    >
                      {t(`transitionsShort.${clip.transitionIn.type}`)}
                    </span>
                  ) : null}
                </p>

                {/* Exact trims by numbers, on the selected part only: edge
                    dragging is for feel, these are for "cut at 12:30". Both
                    edges clamp to the source and the minimum clip length. */}
                {clip.id === selectedId ? (
                  <div
                    className="mt-1.5 flex flex-wrap items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <TimecodeField
                      label={t("elements.from")}
                      valueMs={clip.sourceStartMs}
                      onCommit={(ms) => onTrim(clip.id, "start", ms)}
                    />
                    <TimecodeField
                      label={t("elements.to")}
                      valueMs={clip.sourceEndMs}
                      onCommit={(ms) => onTrim(clip.id, "end", ms)}
                    />
                    <span className="text-[10px] text-text-faint">
                      {t("elements.ofDuration", {
                        duration: formatTimecode(sourceDurations.get(clip.recordingId) ?? 0),
                      })}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
